import { Company } from "../../../api";
import { ImportTableColumn } from "../../../utilities/types";

/**
 * Represents the visibility and required status of a single column
 */
export interface ColumnSetting {
	visible: boolean;
	required: boolean;
}

/**
 * Configuration for columns that have hardcoded visibility/required rules
 */
export interface HardcodedColumnSetting {
	alwaysVisible?: boolean;
	alwaysRequired?: boolean;
}

// ==========================================================================================
// IMPORT_FIELDS: If you are adding a new column, please also add it to all other places with this comment.
// ==========================================================================================
export const columnDisplayNames: Record<ImportTableColumn, string> = {
	grower: "Grower Name",
	farm: "Farm Name",
	field: "Field Name",
	boundaryGeoJSON: "Boundary File",
	pointsGeoJSON: "Points File",
	zonesGeoJSON: "Zone File",
	isReadyToSample: "Ready to Sample",
	deal: "Deal Configuration",
	density: "Density",
	depth: "Depth",
	testPackage: "Test Package",
	frequencyOfMicros: "Frequency of Micros",
	fieldPriority: "Field Priority",
	labSubmissionCode: "Lab Submission Code",
	growerPhoneNumber: "Grower Phone Number",
	submissionNotes: "Submission Notes",
	fieldId: "Field ID",
	microsList: "Sample Test Specifier",
	lastCrop: "Last Crop",
	nextCrop: "Next Crop",
	yieldGoal: "Yield Goal",
	labInstructions: "Instructions for Lab (not for Rogo)",
	dateFieldReadyEstimated: "Estimated Field Ready Date",
	salesperson: "Salesperson",
	salespersonMobilePhone: "Salesperson Mobile Phone",
	branch: "Branch",
	submitterName: "Submitter Name",
	submitterEmail: "Submitter Email",
	eventId: "Event/Sync Code",
	collectBoundary: "Record HD Boundary",
	isTestJob: "Test Job",
	fieldNotes: "Field Notes",
	labName: "Lab Name",
	externalLabId: "External Lab ID",
	externalId: "External ID",
	externalFarmId: "External Farm ID",
};

/**
 * Columns that have hardcoded visibility/required rules that cannot be changed
 */
export const hardcodedColumns: Partial<Record<ImportTableColumn, HardcodedColumnSetting>> = {
	grower: { alwaysVisible: true, alwaysRequired: true },
	farm: { alwaysVisible: true },
	field: { alwaysVisible: true, alwaysRequired: true },
	boundaryGeoJSON: { alwaysVisible: true },
	pointsGeoJSON: { alwaysVisible: true },
	collectBoundary: { alwaysVisible: true },
	deal: { alwaysVisible: true, alwaysRequired: true },
	depth: { alwaysVisible: true, alwaysRequired: true },
	density: { alwaysVisible: true, alwaysRequired: true },
};

/**
 * ViewModel interface for ImportSettings component
 */
export interface ImportSettingsViewModel {
	// State
	companies: Company[];
	selectedCompany: Company | null;
	sourceCompany: Company | null;
	columnSettings: Record<string, ColumnSetting>;
	loading: boolean;
	saving: boolean;
	copying: boolean;

	// Event handlers
	onSelectCompany: (company: Company | null) => void;
	onSelectSourceCompany: (company: Company | null) => void;
	onColumnChange: (column: ImportTableColumn, field: "visible" | "required", value: boolean) => void;
	onSave: () => Promise<void>;
	onCopySettings: () => void;
	onResetToDefaults: () => void;
	onSetAllVisible: (visible: boolean) => void;
	onSetAllRequired: (required: boolean) => void;
	onCancel: () => void;

	// Computed values
	allNonHardcodedVisible: boolean;
	allVisibleNonHardcodedRequired: boolean;
	hasChanges: boolean;

	// Helper functions for view
	isVisibleDisabled: (column: ImportTableColumn) => boolean;
	isRequiredDisabled: (column: ImportTableColumn) => boolean;
	getHardcodedLabel: (column: ImportTableColumn) => string | null;

	// Permission
	isSuperUser: boolean;
}
